import { useMemo, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import { VenueGallery } from "@/components/venues/VenueGallery";
import { VenueReviewCard } from "@/components/venues/VenueReviewCard";
import { VenueStats } from "@/components/venues/VenueStats";
import { VenueSidebar } from "@/components/venues/VenueSidebar";
import { VenueOfficialServices } from "@/components/venues/VenueOfficialServices";
import { GlobalSchema } from "@/components/seo/GlobalSchema";
import { JsonLd } from "@/components/seo/JsonLd";
import { breadcrumbSchema, musicVenueSchema } from "@/components/seo/schemas";
import { useSchemaIndexable } from "@/components/seo/useSchemaIndexable";

// Helper: render venue description (plain text or HTML)
function VenueDescription({ html }: { html: string }) {
  const isHtml = /<[a-z][\s\S]*>/i.test(html);
  if (!isHtml)
    return (
      <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground whitespace-pre-line">
        {html}
      </p>
    );
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["b", "strong", "i", "em", "ul", "ol", "li", "a", "p", "br", "span"],
    ALLOWED_ATTR: ["href", "target", "rel"],
  });
  return (
    <div
      className="prose prose-sm max-w-2xl text-muted-foreground [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 [&_a]:text-primary [&_a]:underline [&_br]:block"
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

export default function VenueDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: venue, isLoading: venueLoading } = useQuery({
    queryKey: ["venue", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("venues")
        .select("*")
        .eq("slug", slug)
        .eq("status", "active")
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  // Redirect to canonical slug if it doesn't match (old broken slug)
  useEffect(() => {
    if (venue && venue.slug && venue.slug !== slug) {
      navigate(`/sala/${venue.slug}/`, { replace: true });
    }
  }, [venue, slug, navigate]);

  const seoVars = useMemo(
    () => ({
      nombre_sala: venue?.name ?? "",
      ciudad: venue?.city ?? "",
      tipo: venue?.type ?? "",
      comunidad: venue?.autonomous_community ?? "",
    }),
    [venue?.name, venue?.city, venue?.type, venue?.autonomous_community]
  );

  useSeoMeta({
    pageType: "sala_detail",
    entityId: slug,
    variables: seoVars,
    canonicalPath: venue?.slug ? `/sala/${venue.slug}/` : undefined,
  });

  // Fetch canonical city slug for breadcrumbs
  const { data: citySlugRecord } = useQuery({
    queryKey: ["city-slug", venue?.city],
    queryFn: async () => {
      const { data } = await supabase
        .from("cities")
        .select("slug")
        .ilike("name", venue!.city)
        .eq("is_active", true)
        .maybeSingle();
      return data;
    },
    enabled: !!venue?.city,
    staleTime: 1000 * 60 * 30,
  });

  // Fetch main image for schema
  const { data: mainImage } = useQuery({
    queryKey: ["venue-main-image", venue?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("venue_images")
        .select("image_url, is_main")
        .eq("venue_id", venue!.id)
        .order("is_main", { ascending: false })
        .order("sort_order", { ascending: true })
        .limit(1)
        .maybeSingle();
      return data?.image_url ?? null;
    },
    enabled: !!venue?.id,
    staleTime: 1000 * 60 * 30,
  });

  const isIndexable = useSchemaIndexable("sala_detail", slug);

  const { data: reviews } = useQuery({
    queryKey: ["venue-reviews", venue?.id],
    queryFn: async () => {
      const { data: reviewsData, error } = await supabase
        .from("reviews")
        .select("*")
        .eq("venue_id", venue!.id)
        .eq("status", "approved")
        .order("created_at", { ascending: false });
      if (error) throw error;
      if (!reviewsData?.length) return reviewsData;

      const userIds = [...new Set(reviewsData.map((r) => r.user_id))];
      const { data: profilesData } = await supabase
        .from("profiles_public" as any)
        .select("id, username, band_name, visible_name, profile_type, avatar_url")
        .in("id", userIds);

      const profileMap = new Map(
        (profilesData ?? []).map((p: any) => [p.id, p])
      );

      return reviewsData.map((r) => ({
        ...r,
        profiles: profileMap.get(r.user_id) ?? null,
      }));
    },
    enabled: !!venue?.id,
  });

  // Schema JSON-LD data (must be before early returns per hooks rules)
  const venueSchemaData = useMemo(() => {
    if (!isIndexable || !venue) return null;
    const reviewCount = reviews?.length ?? 0;
    const rating = reviewCount
      ? reviews!.reduce((sum, r) => sum + (r.overall_rating || 0), 0) / reviewCount
      : null;
    const citySlug = citySlugRecord?.slug ?? venue.city.toLowerCase().replace(/\s+/g, "-");
    const breadcrumbs = breadcrumbSchema([
      { name: "Inicio", path: "/" },
      { name: "Salas", path: "/salas/" },
      { name: venue.city, path: `/salas/${citySlug}/` },
      { name: venue.name, path: `/sala/${venue.slug}/` },
    ]);
    const venueSchema = musicVenueSchema(venue, rating, reviewCount, mainImage);
    return [breadcrumbs, venueSchema];
  }, [isIndexable, venue, reviews, mainImage, citySlugRecord]);

  if (venueLoading) {
    return (
      <Layout>
        <div className="container py-8">
          <Skeleton className="mb-4 h-8 w-48" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      </Layout>
    );
  }

  if (!venue) {
    return (
      <Layout>
        <div className="container py-16 text-center">
          <p className="text-lg font-medium">Sala no encontrada</p>
          <Button asChild variant="link" className="mt-4">
            <Link to="/">Volver al buscador</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const avgRating = reviews?.length
    ? reviews.reduce((sum, r) => sum + (r.overall_rating || 0), 0) / reviews.length
    : null;
  const priceReviews = reviews?.filter((r) => r.price_paid) ?? [];
  const estimatedPrice = priceReviews.length
    ? priceReviews.reduce((sum, r) => sum + (r.price_paid || 0), 0) / priceReviews.length
    : null;

  const stats = reviews?.length
    ? {
        soundTech: reviews.filter((r) => r.sound_tech === true).length,
        technicalRider: reviews.filter((r) => r.technical_rider === true).length,
        merchandising: reviews.filter((r) => r.merchandising_area === true).length,
        ticketing: reviews.filter((r) => r.ticketing_required === true).length,
        backline: reviews.filter((r: any) => r.backline_available === true).length,
        audioRecording: reviews.filter((r: any) => r.audio_recording === true).length,
        posterDesign: reviews.filter((r: any) => r.poster_design === true).length,
        total: reviews.length,
      }
    : null;

  return (
    <Layout>
      <GlobalSchema pageType="sala_detail" entityId={slug} />
      {venueSchemaData && <JsonLd data={venueSchemaData} />}
      <div className="container py-6 md:py-8">
        {/* Breadcrumb */}
        <Link
          to="/"
          className="mb-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Volver al buscador
        </Link>

        {/* 2-column layout: main scrollable + sticky sidebar */}
        <div className="lg:flex lg:gap-8">
          {/* Main column */}
          <div className="flex-1 min-w-0 space-y-10">
            {/* H1 — primary heading for SEO */}
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
                {venue.name} en {venue.city}
              </h1>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Precios, servicios oficiales y datos aportados por la comunidad.
              </p>
            </div>

            {/* Gallery */}
            <VenueGallery venueId={venue.id} />

            {/* Mobile sidebar (visible only on mobile/tablet) */}
            <div className="lg:hidden">
              <VenueSidebar
                venue={venue}
                slug={slug!}
                avgRating={avgRating}
                reviewCount={reviews?.length ?? 0}
                estimatedPrice={estimatedPrice}
                priceReportsCount={priceReviews.length}
                isLoggedIn={!!user}
              />
            </div>

            {/* Description */}
            {venue.description && (
              <section>
                <h2 className="mb-4 font-display text-xl font-bold">
                  Sobre esta sala
                </h2>
                <VenueDescription html={venue.description} />
              </section>
            )}

            {/* Official Services */}
            <VenueOfficialServices venue={venue} />

            {/* Collaborative stats */}
            {stats && stats.total > 0 && <VenueStats stats={stats} />}

            {/* Reviews */}
            <section>
              <div className="mb-6 flex items-center justify-between">
                <h2 className="font-display text-xl font-bold">
                  Reviews ({reviews?.length ?? 0})
                </h2>
                {user && (
                  <Button asChild size="sm" className="rounded-full">
                    <Link to={`/sala/${slug}/review/`}>Escribe tu review</Link>
                  </Button>
                )}
              </div>
              {!reviews?.length ? (
                <p className="text-sm text-muted-foreground">
                  Aún no hay reviews.{" "}
                  {user ? (
                    <Link
                      to={`/sala/${slug}/review/`}
                      className="underline hover:text-foreground"
                    >
                      Sé el primero
                    </Link>
                  ) : (
                    <Link to="/auth" className="underline hover:text-foreground">
                      Inicia sesión para contribuir
                    </Link>
                  )}
                </p>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <VenueReviewCard key={review.id} review={review} />
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* Sticky sidebar (desktop only) */}
          <aside className="hidden lg:block w-[340px] shrink-0">
            <div className="sticky top-[calc(3.5rem+1.5rem)] max-h-[calc(100vh-3.5rem-3rem)] overflow-y-auto">
              <VenueSidebar
                venue={venue}
                slug={slug!}
                avgRating={avgRating}
                reviewCount={reviews?.length ?? 0}
                estimatedPrice={estimatedPrice}
                priceReportsCount={priceReviews.length}
                isLoggedIn={!!user}
              />
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}
